import { FetchResult, MutationFunctionOptions } from '@apollo/client';
import React, { Dispatch, JSX, SetStateAction, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ContactInput, CreativeCredit } from 'sr-types/lib/shortlist/v1/graphql';
import { Filter } from '../../common/components/types';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS, METADATA_NAMES } from '../../common/form/formConstants';
import { FormContainer } from '../../common/form/FormContainer';
import { FormContext } from '../../common/form/FormContext';
import FormItem from '../../common/form/FormItem';
import FormPanel from '../../common/form/FormPanel';
import FormWidget from '../../common/form/FormWidget';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';
import I18n, { useI18n } from '../../common/i18n/I18n';
import { KeyCreditsFormField } from '../../common/keyCredits';
import { toKeyCreditsLineItemRowFromItemInfo } from '../../common/keyCredits/ui/utils';
import { slimSearchQuery } from '../../common/list/slimQuery';
import OrganizationOptionRenderer from '../../organization/OrganizationOptionRenderer';
import { client, saveQuery } from '../shortlist';
import type { ItemInfo } from '../shortlistLineItem';

import {
    AddOptionValue,
    SaveShortlistMutationData,
    SaveShortlistMutationVariables,
    ShortlistReferenceOption
} from '../utils/shortlistUtils';
import PersonOptionRenderer from './PersonOptionRenderer';
import RepresentativeFormItem from './RepresentativeFormItem';

export enum LineItemTypeEnum {
    person = 'Person',
    organization = 'Organization'
}

export type LineItemType = (typeof LineItemTypeEnum)[keyof typeof LineItemTypeEnum];

type ReactElementType = {
    [key in LineItemType]: (props) => JSX.Element;
};

const optionRenderer: ReactElementType = {
    [LineItemTypeEnum.person]: PersonOptionRenderer,
    [LineItemTypeEnum.organization]: OrganizationOptionRenderer
};

const KEY_ROLES_FILTERS = [{ identifier: 'labels', value: 'Security', mustNot: true }];

interface ShortlistLineItemsItemsProps {
    setIsSaveDisabled: Dispatch<SetStateAction<boolean>>;
    addLineItemFilter: Filter;
    alreadyAddedIds: string[];
    inquiry: boolean;
    onCreateHandle: (e) => void;
    quickAddShortList: (
        value: AddOptionValue,
        save: (
            options: MutationFunctionOptions<SaveShortlistMutationData, SaveShortlistMutationVariables>
        ) => Promise<FetchResult<SaveShortlistMutationData>>
    ) => Promise<ShortlistReferenceOption>;
    lineItemEntityType?: LineItemType;
    showAutocomplete?: boolean;
    resourceId?: string;
    isMultiShortlist?: boolean;
    isAIEnabled?: boolean;
    onOpenKeyCreditsAI?: () => void;
    quickAddRepresentativeInitialData?: ContactInput;
    setShowQuickAddPerson?: Dispatch<SetStateAction<boolean>>;
    setQuickAddPersonData?: Dispatch<SetStateAction<Map<string, string>>>;
}

export default ({
    setIsSaveDisabled,
    addLineItemFilter,
    onCreateHandle,
    quickAddShortList,
    lineItemEntityType = undefined,
    alreadyAddedIds = [],
    showAutocomplete = true,
    inquiry = false,
    resourceId = undefined,
    isMultiShortlist = false,
    isAIEnabled = false,
    onOpenKeyCreditsAI,
    setShowQuickAddPerson,
    setQuickAddPersonData,
    quickAddRepresentativeInitialData
}: ShortlistLineItemsItemsProps) => {
    const { state, setState, validation } = useContext(FormContext);

    const handleKeyCreditsChange = useCallback(
        (next: CreativeCredit[]) => {
            setState((prev) => ({ ...prev, keyCredits: next }));
        },
        [setState]
    );

    const keyCreditsLineItemRow = useMemo(
        () =>
            toKeyCreditsLineItemRowFromItemInfo(state?.itemInfo, {
                lineItemEntityType
            }),
        [lineItemEntityType, state?.itemInfo]
    );
    const isPersonLineItem = lineItemEntityType === LineItemTypeEnum.person;
    const isKeyCreditsDisabled = inquiry || (isPersonLineItem && !keyCreditsLineItemRow);
    const lineItemName = state?.itemInfo?.label || state?.itemInfo?.name || state?.name;
    const areRepresentativeFieldsDisabled = !String(lineItemName ?? '').trim();
    const [saveShortList] = useMutationWithContext(saveQuery, client);

    const handleItemInfoChange = useCallback(
        (_fieldName: string, itemInfo: ItemInfo | null | undefined) => {
            setState((prev) => {
                const previousItemId = prev.itemInfo?.ref?.id || prev.itemInfo?.id;
                const nextItemId = itemInfo?.ref?.id || itemInfo?.id;
                const isSameItem =
                    previousItemId || nextItemId
                        ? previousItemId === nextItemId
                        : prev.itemInfo?.label === itemInfo?.label;
                const updatedState = {
                    ...prev,
                    itemInfo,
                    keyRoles: isSameItem ? prev.keyRoles : [...(itemInfo?.professionalRoles ?? [])]
                };
                return updatedState;
            });
        },
        [setState]
    );

    useEffect(() => {
        setIsSaveDisabled(validation && !validation.isValid);
    }, [validation, setIsSaveDisabled]);


    return (
        <FormPanel>
            <FormContainer>
                <FormItem>
                    <FormWidget
                        name="referenceUrl.path"
                        label={<I18n token="form.shortlistLineItem.reference.link" />}
                        placeholder={useI18n('form.shortlistLineItem.website.address')}
                    />
                </FormItem>
                <FormItem>
                    {showAutocomplete &&
                    (lineItemEntityType === LineItemTypeEnum.person ||
                        lineItemEntityType === LineItemTypeEnum.organization) ? (
                        <FormWidget
                            disabled={inquiry}
                            name="itemInfo"
                            value={state?.itemInfo}
                            onChange={handleItemInfoChange}
                            required={!inquiry && Boolean(validation?.required?.itemInfo)}
                            errors={validation?.errors?.itemInfo}
                            label={<I18n token="form.name" />}
                            component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                            addOption
                            allowFreeForm
                            skipReferenceCreationOnNoMatch
                            entity={lineItemEntityType}
                            OptionRenderer={optionRenderer[lineItemEntityType]}
                            onCreateHandle={onCreateHandle}
                            getOption={(item) => {
                                return {
                                    id: item.id,
                                    name: item.name,
                                    role: item.role,
                                    label: item.name,
                                    isSharedInDirectory: item.isSharedInDirectory,
                                    security: item.security,
                                    ref: { ...item.reference, label: item.name },
                                    professionalRoles: item.professionalRoles
                                };
                            }}
                            preload={false}
                            filter={addLineItemFilter}
                            filterById={alreadyAddedIds}
                            sortBy={'hierarchicalCategories_ASC'}
                            notOnlyRef
                            query={
                                lineItemEntityType === LineItemTypeEnum.person
                                    ? slimSearchQuery(LineItemTypeEnum.person, true)
                                    : undefined
                            }
                        />
                    ) : (
                        <FormWidget name="name" label={<I18n token="form.name" />} />
                    )}
                </FormItem>
                <FormItem>
                    <FormWidget
                        name="itemStatus"
                        label={<I18n token="shortlist.line.item.status" />}
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName="ShortlistItemStatus"
                        allowCreate
                    />
                </FormItem>
                <FormItem>
                    <FormWidget
                        name="summary.about"
                        label={<I18n token="shortlist.candidate.summary" />}
                        component={FORM_WIDGET_COMPONENTS.textArea}
                    />
                </FormItem>
                {isMultiShortlist && (
                    <FormItem>
                        <FormWidget
                            name="resourceShortlistName"
                            disabled={Boolean(state?.identity?.id)}
                            label={<I18n token="form.resource.shortlist" />}
                            component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                            entity={ENTITY_CONSTANTS.shortlist}
                            addOption
                            filter={{
                                expressions: [
                                    { field: 'contextType', value: { values: ['Resource'] } },
                                    { field: 'contextId', value: { values: [`${resourceId}`] } }
                                ]
                            }}
                            saveMutation={saveShortList}
                            onCreateHandle={quickAddShortList}
                            preload={false}
                            sortBy={'createdOn_ASC'}
                            autoSelectFirstOption
                            notOnlyRef
                        />
                    </FormItem>
                )}
                <FormItem>
                    <KeyCreditsFormField
                        value={state?.keyCredits}
                        onChange={handleKeyCreditsChange}
                        lineItemRow={keyCreditsLineItemRow}
                        allowFreeTextAdd
                        openDropdownOnInput
                        label={<I18n token="shortlist.line.item.key.credits" />}
                        disabled={isKeyCreditsDisabled}
                        isAIEnabled={isAIEnabled && !isKeyCreditsDisabled}
                        onOpenKeyCreditsAI={onOpenKeyCreditsAI}
                    />
                </FormItem>
                <FormItem>
                    <FormWidget
                        multiple
                        disabled={areRepresentativeFieldsDisabled}
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        name="keyRoles"
                        label={<I18n token="shortlist.line.item.key.roles" />}
                        metadataName={METADATA_NAMES.role}
                        filters={KEY_ROLES_FILTERS}
                        allowCreate
                    />
                </FormItem>
                <RepresentativeFormItem
                    directAddContact={false}
                    setShowQuickAddPerson={setShowQuickAddPerson}
                    setQuickAddPersonData={setQuickAddPersonData}
                    quickAddRepresentativeInitialData={quickAddRepresentativeInitialData}
                    disabledFields={areRepresentativeFieldsDisabled}
                    disabledFieldsTooltip={<I18n token="form.shortlistLineItem.representative.disabled.tooltip" />}
                />
            </FormContainer>
        </FormPanel>
    );
};
